// src/pages/veci/PedidosVeci.js
//
// Lista de pedidos del veci contra el backend real.
// Polling cada 15 segundos para reflejar cambios de estado sin refresh.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, CardActionArea, Chip, Alert,
  CircularProgress, Grid, Divider, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChatIcon      from '@mui/icons-material/Chat';
import CancelIcon    from '@mui/icons-material/Cancel';
import RefreshIcon   from '@mui/icons-material/Refresh';

import { pedidos as apiPedidos } from '../../api/pedidosApi';
import { formatoCOP } from '../../utils/format';

const ESTADO_COLOR = {
  nuevo:       'default',
  preparacion: 'info',
  en_ruta:     'warning',
  entregado:   'success',
  cancelado:   'error',
};
const ESTADO_LABEL = {
  nuevo:       'Nuevo',
  preparacion: 'En preparación',
  en_ruta:     'En ruta',
  entregado:   'Entregado',
  cancelado:   'Cancelado',
};

export default function PedidosVeci() {
  const navigate = useNavigate();
  const [items, setItems]         = useState([]);
  const [cargando, setCargando]   = useState(true);
  const [error, setError]         = useState('');
  const [detalle, setDetalle]     = useState(null);
  const [cancelando, setCancelando] = useState(null); // { pedidoId, motivo }

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await apiPedidos.listar({ pageSize: 50 });
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los pedidos.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 15000);
    return () => clearInterval(id);
  }, [cargar]);

  const abrirDetalle = async (pedidoId) => {
    try {
      const d = await apiPedidos.detalle(pedidoId);
      setDetalle(d);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cargar el detalle.');
    }
  };

  const confirmarCancelar = async () => {
    try {
      await apiPedidos.cancelar(cancelando.pedidoId, cancelando.motivo || '');
      setCancelando(null);
      cargar();
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo cancelar.');
    }
  };

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Mis pedidos</Typography>
          <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Mis pedidos</Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={cargar}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Aún no has hecho pedidos. Explora el directorio y arma tu primera compra.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/veci')}>Ver directorio</Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {items.map((p) => (
            <Card key={p.pedidoId || p.PedidoId}>
              <CardActionArea onClick={() => abrirDetalle(p.pedidoId || p.PedidoId)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="overline" color="text.secondary">
                        {p.numeroPedido || p.NumeroPedido}
                      </Typography>
                      <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>
                        {p.negocioNombre || p.NegocioNombre}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(p.fecha || p.Fecha).toLocaleString('es-CO', {
                          dateStyle: 'medium', timeStyle: 'short',
                        })}
                      </Typography>
                    </Box>
                    <Chip
                      label={ESTADO_LABEL[p.estado || p.Estado] || (p.estado || p.Estado)}
                      color={ESTADO_COLOR[p.estado || p.Estado] || 'default'}
                    />
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2">
                      {p.cantidadItems || p.CantidadItems} producto(s)
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'primary.main' }}>
                      {formatoCOP(p.total || p.Total)}
                    </Typography>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}

      {/* Dialog detalle */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="overline" color="text.secondary">{detalle?.numeroPedido || detalle?.NumeroPedido}</Typography>
          <Typography variant="h5" sx={{ fontFamily: 'Fraunces' }}>{detalle?.negocioNombre || detalle?.NegocioNombre}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detalle && (
            <Stack spacing={2}>
              <Chip
                label={ESTADO_LABEL[detalle.estado || detalle.Estado]}
                color={ESTADO_COLOR[detalle.estado || detalle.Estado] || 'default'}
                sx={{ alignSelf: 'flex-start' }}
              />

              <Box>
                <Typography variant="overline" color="text.secondary">Entrega</Typography>
                <Typography variant="body2">{detalle.direccionEntrega || detalle.DireccionEntrega}</Typography>
                {(detalle.instrucciones || detalle.Instrucciones) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {detalle.instrucciones || detalle.Instrucciones}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">Productos</Typography>
                {(detalle.items || detalle.Items || []).map((it, i) => (
                  <Box key={i} sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {it.cantidad || it.Cantidad}× {it.nombre || it.Nombre}
                    </Typography>
                    <Typography variant="body2">{formatoCOP(it.subtotal || it.Subtotal)}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  {formatoCOP(detalle.total || detalle.Total)}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            startIcon={<ChatIcon />}
            onClick={() => {
              const negId = detalle?.negocioId || detalle?.NegocioId;
              setDetalle(null);
              navigate(`/veci/mensajes/${negId}`);
            }}
          >
            Chat
          </Button>
          {['nuevo'].includes(detalle?.estado || detalle?.Estado) && (
            <Button
              startIcon={<CancelIcon />}
              color="error"
              onClick={() => setCancelando({ pedidoId: detalle.pedidoId || detalle.PedidoId, motivo: '' })}
            >
              Cancelar
            </Button>
          )}
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar cancelar */}
      <Dialog open={!!cancelando} onClose={() => setCancelando(null)}>
        <DialogTitle>Cancelar pedido</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Solo puedes cancelar mientras el pedido está en estado "Nuevo".
          </Typography>
          <TextField
            fullWidth
            label="Motivo (opcional)"
            value={cancelando?.motivo || ''}
            onChange={(e) => setCancelando(s => ({ ...s, motivo: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelando(null)}>Volver</Button>
          <Button onClick={confirmarCancelar} color="error" variant="contained">Cancelar pedido</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
